import { validarEntorno } from '../helpers/validarHost';

// Canal de comunicación entre pestañas
let broadcastChannel = null;
try {
  broadcastChannel = new BroadcastChannel('chat_sync');
} catch (error) {
  console.warn('BroadcastChannel not supported:', error);
}

// Función para notificar cambios a otras pestañas
const notifyOtherTabs = (action, data) => {
  if (broadcastChannel) {
    try {
      broadcastChannel.postMessage({
        type: action,
        payload: data,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Error sending broadcast message:', error);
    }
  }
};

// Función para limpiar sesiones antiguas (más de 24 horas)
const cleanOldSessions = () => {
  try {
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 horas
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('chat_messages_chat_session_')) {
        const sessionTimestamp = key.split('_')[3];
        if (sessionTimestamp && (now - parseInt(sessionTimestamp)) > maxAge) {
          localStorage.removeItem(key);
          i--; // Ajustar índice porque se removió un item
        }
      }
    }
  } catch (error) {
    console.error('Error cleaning old sessions:', error);
  }
};

// Generar o recuperar ID de sesión único (simplificado para compartir entre pestañas)
const getSessionId = () => {
  let sessionId = localStorage.getItem('chat_session_id');
  if (!sessionId) {
    // Solo crear nuevo ID si no existe uno
    sessionId = 'chat_session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chat_session_id', sessionId);
  }
  
  // Marcar que esta pestaña está activa (para cleanup al cerrar navegador)
  sessionStorage.setItem('browser_session_active', 'true');
  
  return sessionId;
};

// Función para cargar mensajes desde localStorage con ID de sesión
const loadMessagesFromSession = () => {
  try {
    const sessionId = getSessionId();
    const savedMessages = localStorage.getItem(`chat_messages_${sessionId}`);
    return savedMessages ? JSON.parse(savedMessages) : [];
  } catch (error) {
    console.error('Error loading messages from localStorage:', error);
    return [];
  }
};

// Función para guardar mensajes en localStorage con ID de sesión
const saveMessagesToSession = (messages) => {
  try {
    const sessionId = getSessionId();
    localStorage.setItem(`chat_messages_${sessionId}`, JSON.stringify(messages));
    
    // Notificar a otras pestañas sobre el cambio
    notifyOtherTabs('MESSAGES_UPDATED', messages);
  } catch (error) {
    console.error('Error saving messages to localStorage:', error);
  }
};

// Función para limpiar mensajes de localStorage
const clearMessagesFromSession = () => {
  try {
    const sessionId = localStorage.getItem('chat_session_id');
    if (sessionId) {
      localStorage.removeItem(`chat_messages_${sessionId}`);
      localStorage.removeItem('chat_session_id'); // Remover también el ID de sesión
      sessionStorage.removeItem('browser_session_active'); // Limpiar marcador de sesión
      
      // Notificar a otras pestañas sobre la limpieza
      notifyOtherTabs('MESSAGES_CLEARED', null);
      
      // También limpiar sesiones antiguas al hacer logout
      cleanOldSessions();
    }
  } catch (error) {
    console.error('Error clearing messages from localStorage:', error);
  }
};

// Estado inicial
export const initialChatState = {
  messages: loadMessagesFromSession(), // Cargar directamente sin limpiar sesiones
  isLoading: false,
  error: null,
  isOpen: false,
  mode: 'ia', // 'ia' | 'interno'
  userId: null,
  chatConfig: {
    endpoint: 'https://www.ciacosinte.site/cia2_google/JSON/CriterIA/chat_client.php',
    theme: 'default'
  },
  typingUsers: [],
  unreadCount: 0
};

// Tipos de acciones
export const CHAT_ACTIONS = {
  SET_CONFIG: 'SET_CONFIG',
  TOGGLE_CHAT: 'TOGGLE_CHAT',
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_MESSAGES: 'SET_MESSAGES',
  SET_TYPING_USERS: 'SET_TYPING_USERS',
  INCREMENT_UNREAD: 'INCREMENT_UNREAD',
  CLEAR_UNREAD: 'CLEAR_UNREAD',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  LOGOUT: 'LOGOUT',
  SYNC_FROM_OTHER_TAB: 'SYNC_FROM_OTHER_TAB'
};

// Reducer
export const chatReducer = (state, action) => {
  switch (action.type) {
    case CHAT_ACTIONS.SET_CONFIG:
      return {
        ...state,
        chatConfig: { ...state.chatConfig, ...action.payload },
        mode: action.payload.mode || state.mode,
        userId: action.payload.userId || state.userId
      };

    case CHAT_ACTIONS.TOGGLE_CHAT:
      return {
        ...state,
        isOpen: !state.isOpen,
        unreadCount: !state.isOpen ? 0 : state.unreadCount
      };

    case CHAT_ACTIONS.ADD_MESSAGE:
      const newMessagesAdd = [...state.messages, action.payload];
      saveMessagesToSession(newMessagesAdd);
      return {
        ...state,
        messages: newMessagesAdd,
        unreadCount: state.isOpen ? state.unreadCount : state.unreadCount + 1
      };

    case CHAT_ACTIONS.UPDATE_MESSAGE:
      const updatedMessages = state.messages.map(msg => 
        msg.id === action.payload.id 
          ? { ...msg, ...action.payload }
          : msg
      );
      saveMessagesToSession(updatedMessages);
      return {
        ...state,
        messages: updatedMessages
      };

    case CHAT_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case CHAT_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };

    case CHAT_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    case CHAT_ACTIONS.SET_MESSAGES:
      saveMessagesToSession(action.payload);
      return {
        ...state,
        messages: action.payload
      };

    case CHAT_ACTIONS.SET_TYPING_USERS:
      return {
        ...state,
        typingUsers: action.payload
      };

    case CHAT_ACTIONS.INCREMENT_UNREAD:
      return {
        ...state,
        unreadCount: state.unreadCount + 1
      };

    case CHAT_ACTIONS.CLEAR_UNREAD:
      return {
        ...state,
        unreadCount: 0
      };

    case CHAT_ACTIONS.CLEAR_MESSAGES:
      clearMessagesFromSession();
      return {
        ...state,
        messages: []
      };

    case CHAT_ACTIONS.LOGOUT:
      clearMessagesFromSession();
      return {
        ...initialChatState,
        messages: []
      };

    case CHAT_ACTIONS.SYNC_FROM_OTHER_TAB:
      // Sincronizar mensajes desde otra pestaña sin guardar (evita loop infinito)
      return {
        ...state,
        messages: action.payload || []
      };

    default:
      return state;
  }
};

// Action creators
export const setChatConfig = (config) => ({
  type: CHAT_ACTIONS.SET_CONFIG,
  payload: config
});

export const toggleChat = () => ({
  type: CHAT_ACTIONS.TOGGLE_CHAT
});

export const addMessage = (message) => ({
  type: CHAT_ACTIONS.ADD_MESSAGE,
  payload: {
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    ...message
  }
});

export const setLoading = (isLoading) => ({
  type: CHAT_ACTIONS.SET_LOADING,
  payload: isLoading
});

export const setError = (error) => ({
  type: CHAT_ACTIONS.SET_ERROR,
  payload: error
});

export const clearError = () => ({
  type: CHAT_ACTIONS.CLEAR_ERROR
});

export const setMessages = (messages) => ({
  type: CHAT_ACTIONS.SET_MESSAGES,
  payload: messages
});

export const clearMessages = () => ({
  type: CHAT_ACTIONS.CLEAR_MESSAGES
});

export const clearChatManually = () => ({
  type: CHAT_ACTIONS.CLEAR_MESSAGES
});

export const logoutChat = () => ({
  type: CHAT_ACTIONS.LOGOUT
});

export const syncFromOtherTab = (messages) => ({
  type: CHAT_ACTIONS.SYNC_FROM_OTHER_TAB,
  payload: messages
});

// Exportar canal de comunicación y funciones de persistencia para uso externo
export { clearMessagesFromSession, saveMessagesToSession, loadMessagesFromSession, broadcastChannel };

// Thunks para acciones asíncronas
export const sendMessage = (message, dispatch, state) => async () => {
  try {
    dispatch(setLoading(true));
    dispatch(clearError());

    // Agregar mensaje del usuario inmediatamente
    const userMessage = {
      text: message,
      sender: 'user',
      userId: state.userId,
      type: 'text'
    };
    dispatch(addMessage(userMessage));

    if (state.mode === 'ia') {
      // Modo IA - usar streaming
      await sendMessageToAI(message, dispatch, state);
    } else {
      // Modo interno - enviar a otros usuarios
      await sendMessageToUsers(message, dispatch, state);
    }

    dispatch(setLoading(false));
  } catch (error) {
    dispatch(setError(error.message));
    dispatch(setLoading(false));
  }
};

// Función para enviar mensaje a IA
const sendMessageToAI = async (message, dispatch, state) => {
  try {
    const endpoint = state.endpoint || state.chatConfig?.endpoint || 'https://www.ciacosinte.site/cia2_google/JSON/CriterIA/chat_client.php';
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        userId: state.userId, 
        message: message 
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Server Error: ${response.status}`);
    }

    // Verificar si la respuesta es streaming (text/plain) o JSON (error)
    const contentType = response.headers.get('content-type');
    
    // Crear mensaje inicial para la IA
    const aiMessageId = Date.now() + Math.random();
    const aiMessage = {
      id: aiMessageId,
      text: '',
      sender: 'ai',
      userId: 'abigail-cia',
      type: 'text',
      isStreaming: true,
      timestamp: new Date().toISOString(),
      status: 'sent'
    };
    
    dispatch(addMessage(aiMessage));

    if (contentType && contentType.includes('text/plain')) {
      // Procesar respuesta en streaming
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiResponse = '';

      // Leer stream
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        aiResponse += chunk;
        
        // Actualizar mensaje de IA usando una acción específica
        dispatch({
          type: CHAT_ACTIONS.UPDATE_MESSAGE,
          payload: { id: aiMessageId, text: aiResponse }
        });
      }
    } else {
      // Respuesta no streaming (posible error o respuesta completa)
      const text = await response.text();
      dispatch({
        type: CHAT_ACTIONS.UPDATE_MESSAGE,
        payload: { id: aiMessageId, text: text }
      });
    }

    // Marcar como completado
    dispatch({
      type: CHAT_ACTIONS.UPDATE_MESSAGE,
      payload: { id: aiMessageId, isStreaming: false }
    });

  } catch (error) {
    throw error;
  }
};

// Función para enviar mensaje a usuarios internos
const sendMessageToUsers = async (message, dispatch, state) => {
  try {
    const response = await validarEntorno('JSON/chat_interno.html', 'POST', {
      userId: state.userId,
      message: message,
      hash: window.location.hash
    });

    if (response.ok) {
      const data = await response.json();
      // Procesar respuesta del servidor
      if (data.success) {
        // Mensaje enviado exitosamente
      }
    } else {
      throw new Error('Error al enviar mensaje a usuarios internos');
    }
  } catch (error) {
    throw error;
  }
};
